/**
 * Calculator Visibility Controller
 * Hides the custom pricing calculator on page load if product has multiple variants
 * Shows it only after a specific variant is selected (via variantSelectionChanged event)
 *
 * This controller leverages the variantSelectionChanged custom event that is dispatched
 * by validateAddToCartButton() in scripts.js, which fires when variant dropdowns
 * are changed by the user.
 */

const _calcVisibilityLog = typeof window.createLogger === 'function'
  ? window.createLogger('CalculatorVisibility')
  : { log: () => {}, warn: () => {}, error: () => {} };

class CalculatorVisibilityController {
  constructor() {
    this.calculatorSelector = '#calculator-form-container';
    this.stockAvailabilitySelector = '#product-stock-availability';
    this.stockShippingDateSelector = '#product-stock-shipping-date';
    this.priceContainerSelector = '.price-container';

    _calcVisibilityLog.log('Initializing...');

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    _calcVisibilityLog.log('init() called');

    // Wait a bit for third-party app to inject the calculator
    setTimeout(() => {
      this.setupInitialVisibility();
      this.setupVariantSelectionListener();
      this.setupCalculatorFieldListeners();
    }, 500);
  }

  /**
   * Check if calculator is actually rendered by third-party app (has content injected)
   * Note: We control visibility with hideCalculator/showCalculator, so don't check display styles here
   */
  isCalculatorRendered() {
    const calculator = document.querySelector(this.calculatorSelector);

    if (!calculator) {
      _calcVisibilityLog.log('Calculator element not found in DOM');
      return false;
    }

    // Check if calculator has any meaningful content (third-party app has injected content)
    // Look for the actual calculator form, not just the container
    const calcForm = calculator.querySelector('#calc-form-preview, .calculator-form-container');
    const hasContent = calcForm && calcForm.children.length > 0;

    // Also check if the calculator has fields (more specific check)
    const hasFields = calculator.querySelector('.calc-field, input[name="length"], input[name="width"]') !== null;

    const isRendered = hasContent || hasFields;

    _calcVisibilityLog.log('Calculator rendered check:', { exists: true, hasContent, hasFields, isRendered });

    return isRendered;
  }

  /**
   * Hide calculator on initial page load if product has variants
   */
  setupInitialVisibility() {
    // Count how many variant option dropdowns exist
    const variantSelects = document.querySelectorAll('select[name^="options"]');
    const dropdownCount = variantSelects.length;

    _calcVisibilityLog.log('Number of variant dropdowns:', dropdownCount);

    // Hide calculator on initial load if dropdowns exist
    // (meaning customer needs to select options before calculator should show)
    if (dropdownCount > 0) {
      this.hideCalculator();
      this.hideStockAvailability(); // Hide stock initially, will show when Add to Cart is enabled
      _calcVisibilityLog.log('Calculator hidden on load - awaiting variant selection');
    } else {
      // No dropdowns = single variant product
      // Check if calculator is actually rendered by third-party app
      const calculatorRendered = this.isCalculatorRendered();

      if (calculatorRendered) {
        // Calculator is rendered - show it, hide stock
        this.showCalculator();
        this.hideStockAvailability();
        _calcVisibilityLog.log('Single variant product - calculator shown');
      } else {
        // Calculator not rendered by third-party app - show stock availability if Add to Cart is enabled
        const addButton = document.querySelector('.button.button-secondary.button-add-to-cart');
        const isAddButtonEnabled = addButton && !addButton.hasAttribute('disabled');

        if (isAddButtonEnabled) {
          this.showStockAvailability();
          _calcVisibilityLog.log('Single variant product - stock availability shown (calculator not rendered)');
        } else {
          this.hideStockAvailability();
          _calcVisibilityLog.log('Single variant product - stock hidden (Add to Cart disabled)');
        }
      }
    }
  }

  /**
   * Listen for variantSelectionChanged event (fired by validateAddToCartButton in scripts.js)
   * This event fires whenever dropdown selections change
   *
   * Additionally checks if the Add to Cart button is enabled before showing calculator/stock
   */
  setupVariantSelectionListener() {
    document.addEventListener('variantSelectionChanged', (event) => {
      _calcVisibilityLog.log('variantSelectionChanged event received');

      // Check if Add to Cart button is enabled
      const addButton = document.querySelector('.button.button-secondary.button-add-to-cart');
      const isAddButtonEnabled = addButton && !addButton.hasAttribute('disabled');

      _calcVisibilityLog.log('Add to Cart button enabled:', isAddButtonEnabled);

      // Only show calculator/stock if Add to Cart button is enabled
      if (isAddButtonEnabled) {
        // Check if calculator is actually rendered by third-party app
        const calculatorRendered = this.isCalculatorRendered();

        if (calculatorRendered) {
          // Calculator is rendered - show it, hide stock
          this.showCalculator();
          this.hideStockAvailability();
          _calcVisibilityLog.log('Calculator shown - stock availability hidden');
        } else {
          // Calculator not rendered by third-party app - show stock availability instead
          this.hideCalculator(); // Hide calculator container if it exists
          this.showStockAvailability();
          _calcVisibilityLog.log('Stock availability shown - calculator not rendered by third-party app');
        }
      } else {
        // Add to Cart button disabled - hide both
        this.hideCalculator();
        this.hideStockAvailability();
        _calcVisibilityLog.log('Both calculator and stock hidden - Add to Cart button disabled');
      }
    });
  }

  /**
   * Hide calculator with fade out effect
   */
  hideCalculator() {
    const calculator = document.querySelector(this.calculatorSelector);

    if (!calculator) {
      _calcVisibilityLog.log('Calculator not found in DOM');
      return;
    }

    calculator.style.display = 'none';
    calculator.setAttribute('data-hidden', 'true');
    calculator.classList.add('calculator-hidden');

    // When hiding calculator, show price container (for non-calculator products)
    this.showPriceContainer();

    _calcVisibilityLog.log('Calculator hidden');
  }

  /**
   * Show calculator with fade in effect
   */
  showCalculator() {
    const calculator = document.querySelector(this.calculatorSelector);

    if (!calculator) {
      _calcVisibilityLog.log('Calculator not found in DOM');
      return;
    }

    calculator.style.display = 'flex';
    calculator.removeAttribute('data-hidden');
    calculator.classList.remove('calculator-hidden');
    _calcVisibilityLog.log('Calculator shown');

    // When showing calculator, hide price container and disable Add to Cart until both fields are filled
    this.hidePriceContainer();
    this.validateCalculatorFields();
  }

  /**
   * Check if calculator fields (length and width) are filled with valid values
   * Disable/enable Add to Cart button accordingly
   */
  validateCalculatorFields() {
    const calculatorRendered = this.isCalculatorRendered();

    if (!calculatorRendered) {
      _calcVisibilityLog.log('Calculator not rendered - skipping field validation');
      return;
    }

    const lengthInput = document.querySelector('input[name="length"]');
    const widthInput = document.querySelector('input[name="width"]');
    const addButton = document.querySelector('.button.button-secondary.button-add-to-cart');

    if (!lengthInput || !widthInput || !addButton) {
      _calcVisibilityLog.log('Calculator fields or Add to Cart button not found');
      return;
    }

    const lengthValue = parseFloat(lengthInput.value);
    const widthValue = parseFloat(widthInput.value);

    // Check if both fields have valid positive values
    const bothFieldsFilled = lengthValue > 0 && widthValue > 0;

    if (bothFieldsFilled) {
      // Enable Add to Cart button and show price
      addButton.removeAttribute('disabled');
      const spanElement = addButton.querySelector('span');
      if (spanElement) {
        spanElement.textContent = 'Add to cart';
      }
      this.showPriceContainer();
      _calcVisibilityLog.log('Calculator fields filled - Add to Cart enabled, price shown');
    } else {
      // Disable Add to Cart button and hide price
      addButton.setAttribute('disabled', 'disabled');
      const spanElement = addButton.querySelector('span');
      if (spanElement) {
        spanElement.textContent = 'Enter dimensions';
      }
      this.hidePriceContainer();
      _calcVisibilityLog.log('Calculator fields empty - Add to Cart disabled, price hidden');
    }
  }

  /**
   * Setup input event listeners on calculator fields to validate when user types
   */
  setupCalculatorFieldListeners() {
    // Use event delegation since calculator fields may not exist yet
    document.addEventListener('input', (event) => {
      const target = event.target;

      // Check if the input is a calculator field
      if (target && (target.name === 'length' || target.name === 'width')) {
        _calcVisibilityLog.log('Calculator field changed:', target.name, '=', target.value);

        // Validate fields after a short delay to allow third-party app to process
        setTimeout(() => {
          this.validateCalculatorFields();
        }, 100);
      }
    });

    _calcVisibilityLog.log('Calculator field listeners setup complete');
  }

  /**
   * Hide stock availability div
   */
  hideStockAvailability() {
    const stockDiv = document.querySelector(this.stockAvailabilitySelector);
    const stockShippingDateDiv = document.querySelector(this.stockShippingDateSelector);

    if (!stockDiv) {
      _calcVisibilityLog.log('Stock availability div not found in DOM');
      return;
    }
    stockDiv.style.display = 'none';
    if (stockShippingDateDiv) {
      stockShippingDateDiv.style.display = 'none';
    }
    _calcVisibilityLog.log('Stock availability hidden');
  }

  /**
   * Show stock availability div
   */
  showStockAvailability() {
    const stockDiv = document.querySelector(this.stockAvailabilitySelector);
    const stockShippingDateDiv = document.querySelector(this.stockShippingDateSelector);

    if (!stockDiv) {
      _calcVisibilityLog.log('Stock availability div not found in DOM');
      return;
    }
    stockDiv.style.display = 'block';

    if (stockShippingDateDiv) {
      stockShippingDateDiv.style.display = 'block';
    }
    _calcVisibilityLog.log('Stock availability shown');
  }

  /**
   * Hide price container (for products with calculator - hide until dimensions entered)
   */
  hidePriceContainer() {
    const priceContainer = document.querySelector(this.priceContainerSelector);

    if (!priceContainer) {
      _calcVisibilityLog.log('Price container not found in DOM');
      return;
    }

    priceContainer.style.display = 'none';
    _calcVisibilityLog.log('Price container hidden');
  }

  /**
   * Show price container (when calculator fields are filled)
   */
  showPriceContainer() {
    const priceContainer = document.querySelector(this.priceContainerSelector);

    if (!priceContainer) {
      _calcVisibilityLog.log('Price container not found in DOM');
      return;
    }

    priceContainer.style.display = 'block';
    _calcVisibilityLog.log('Price container shown');
  }
}

// Initialize when script loads
new CalculatorVisibilityController();
